import { useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Crown, Loader2, Plus, RefreshCw, Shield, UserCog } from 'lucide-react';
import { Button } from '@/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/card';
import { Input } from '@/ui/input';
import { Badge } from '@/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import type { AdminSiteOption, AdminUserRecord } from '@/types/admin';

function formatSites(user: AdminUserRecord, sites: AdminSiteOption[]): string {
  if (user.isSuperAdmin) {
    return 'Super admin — full Tools Hub access';
  }
  if (!user.allowedSiteIds?.length) {
    return 'No websites';
  }
  return user.allowedSiteIds
    .map((id) => {
      const site = sites.find((s) => s.id === id);
      return site ? `${site.label} (${site.domain})` : id;
    })
    .join(', ');
}

function SiteAccessPicker({
  sites,
  selectedSiteIds,
  setSelectedSiteIds,
  disabled,
}: {
  sites: AdminSiteOption[];
  selectedSiteIds: string[];
  setSelectedSiteIds: (ids: string[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-3 rounded-md border p-3">
      <p className="text-sm font-medium">Website access</p>
      <p className="text-xs text-muted-foreground">
        Choose which websites this person can work with in Tools Hub.
      </p>
      <div className="space-y-2">
        {sites.map((site) => (
          <label key={site.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selectedSiteIds.includes(site.id)}
              disabled={disabled}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedSiteIds([...selectedSiteIds, site.id]);
                } else {
                  setSelectedSiteIds(selectedSiteIds.filter((id) => id !== site.id));
                }
              }}
              className="h-4 w-4"
            />
            {site.label} ({site.domain})
          </label>
        ))}
      </div>
    </div>
  );
}

export default function SuperAdminPage() {
  const { user: currentUser, refresh: refreshAuth } = useAuth();
  const {
    users,
    sites,
    loading,
    error,
    refresh,
    createUser,
    updateUser,
    requestSuperAdminTransfer,
    confirmSuperAdminTransfer,
  } = useAdminUsers();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [transferUser, setTransferUser] = useState<AdminUserRecord | null>(null);
  const [transferStep, setTransferStep] = useState<'confirm' | 'otp'>('confirm');
  const [transferOtp, setTransferOtp] = useState('');
  const [transferTargetEmail, setTransferTargetEmail] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [newPassword, setNewPassword] = useState('');

  const editingUser = useMemo(
    () => users.find((u) => u.id === editingId) ?? null,
    [users, editingId]
  );

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setSelectedSiteIds([]);
    setNewPassword('');
  };

  const openEdit = (user: AdminUserRecord) => {
    setEditingId(user.id);
    setDisplayName(user.displayName || '');
    setSelectedSiteIds(user.allowedSiteIds ?? []);
    setNewPassword('');
    setShowCreate(false);
  };

  const closeTransferDialog = () => {
    setTransferUser(null);
    setTransferStep('confirm');
    setTransferOtp('');
    setTransferTargetEmail('');
    setTransferSubmitting(false);
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await createUser({
        email,
        password,
        displayName: displayName || undefined,
        allowedSiteIds: selectedSiteIds.length > 0 ? selectedSiteIds : undefined,
      });
      toast.success('User created');
      resetForm();
      setShowCreate(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (event: FormEvent) => {
    event.preventDefault();
    if (!editingId || !editingUser) return;
    setSubmitting(true);
    try {
      await updateUser(editingId, {
        displayName,
        ...(editingUser.isSuperAdmin
          ? {}
          : { allowedSiteIds: selectedSiteIds.length > 0 ? selectedSiteIds : [] }),
        ...(newPassword ? { password: newPassword } : {}),
      });
      toast.success('User updated');
      setEditingId(null);
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update user');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (user: AdminUserRecord) => {
    if (currentUser && user.id === currentUser.id) {
      toast.error('You cannot deactivate your own account. Transfer super admin access first.');
      return;
    }
    if (user.isSuperAdmin) {
      toast.error('Transfer super admin access before deactivating this account.');
      return;
    }
    try {
      await updateUser(user.id, { isActive: !user.isActive });
      toast.success(user.isActive ? 'User deactivated' : 'User activated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update user');
    }
  };

  const handleSendTransferCode = async () => {
    if (!transferUser) return;
    setTransferSubmitting(true);
    try {
      const result = await requestSuperAdminTransfer(transferUser.id);
      setTransferTargetEmail(result.targetEmail);
      setTransferStep('otp');
      toast.success(`Verification code sent to ${result.targetEmail}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send verification code');
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleConfirmTransfer = async (event: FormEvent) => {
    event.preventDefault();
    if (!transferUser) return;
    setTransferSubmitting(true);
    try {
      await confirmSuperAdminTransfer(transferUser.id, transferOtp);
      await refreshAuth();
      toast.success(`${transferUser.email} is now the super admin`);
      closeTransferDialog();
      setEditingId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not complete transfer');
    } finally {
      setTransferSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold sm:text-xl">
            <Shield className="h-5 w-5" />
            Team & access
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage who can use Tools Hub and which websites they can work with. Only one super admin
            exists at a time.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refresh()}
            disabled={loading}
            className="w-full justify-center sm:w-auto"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            className="w-full justify-center sm:w-auto"
            onClick={() => {
              setShowCreate(true);
              setEditingId(null);
              resetForm();
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add user
          </Button>
        </div>
      </div>

      {error ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="pt-6 text-sm text-destructive">{error}</CardContent>
        </Card>
      ) : null}

      {showCreate ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">New user</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4 sm:max-w-lg">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                type="password"
                placeholder="Password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <Input
                placeholder="Display name (optional)"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <SiteAccessPicker
                sites={sites}
                selectedSiteIds={selectedSiteIds}
                setSelectedSiteIds={setSelectedSiteIds}
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create user
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {editingUser ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Edit {editingUser.email}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdate} className="space-y-4 max-w-lg">
              {editingUser.isSuperAdmin ? (
                <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                  This person is the super admin. To give someone else super admin access, use
                  &quot;Transfer super admin&quot; on their account. You will become a normal admin
                  with access to all websites.
                </p>
              ) : (
                <SiteAccessPicker
                  sites={sites}
                  selectedSiteIds={selectedSiteIds}
                  setSelectedSiteIds={setSelectedSiteIds}
                />
              )}
              <Input
                placeholder="Display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <Input
                type="password"
                placeholder="New password (leave blank to keep current)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={8}
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save changes
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditingId(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UserCog className="h-4 w-4" />
            Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading users…
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No users yet.</p>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Access</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Last login</th>
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isCurrentUser = currentUser?.id === user.id;
                    const canTransfer =
                      currentUser?.isSuperAdmin && !user.isSuperAdmin && user.isActive;
                    return (
                      <tr key={user.id} className="border-t">
                        <td className="p-2 font-medium">
                          <span className="inline-flex flex-wrap items-center gap-2">
                            {user.email}
                            {user.isSuperAdmin ? (
                              <Badge className="text-xs">
                                <Crown className="mr-1 h-3 w-3" />
                                Super admin
                              </Badge>
                            ) : null}
                            {isCurrentUser ? (
                              <Badge variant="secondary" className="text-xs">
                                You
                              </Badge>
                            ) : null}
                          </span>
                        </td>
                        <td className="p-2">{user.displayName || '—'}</td>
                        <td className="p-2 text-muted-foreground">{formatSites(user, sites)}</td>
                        <td className="p-2">
                          {user.isActive ? (
                            <Badge variant="outline" className="text-green-600">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-600">
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td className="p-2 whitespace-nowrap text-muted-foreground">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-2">
                            <Button variant="outline" size="sm" onClick={() => openEdit(user)}>
                              Edit
                            </Button>
                            {canTransfer ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setTransferUser(user);
                                  setTransferStep('confirm');
                                  setTransferOtp('');
                                  setTransferTargetEmail('');
                                }}
                              >
                                Transfer super admin
                              </Button>
                            ) : null}
                            {!isCurrentUser && !user.isSuperAdmin ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => void toggleActive(user)}
                              >
                                {user.isActive ? 'Deactivate' : 'Activate'}
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={transferUser !== null} onOpenChange={(open) => !open && closeTransferDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer super admin</DialogTitle>
            <DialogDescription>
              {transferStep === 'confirm'
                ? `Make ${transferUser?.email} the super admin? A verification code will be sent to their email. You will become a normal admin with access to all websites.`
                : `Enter the 6-digit code sent to ${transferTargetEmail || transferUser?.email}.`}
            </DialogDescription>
          </DialogHeader>

          {transferStep === 'confirm' ? (
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeTransferDialog}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={transferSubmitting}
                onClick={() => void handleSendTransferCode()}
              >
                {transferSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Send verification code
              </Button>
            </DialogFooter>
          ) : (
            <form onSubmit={handleConfirmTransfer} className="space-y-4">
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code"
                value={transferOtp}
                onChange={(e) => setTransferOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                minLength={6}
                maxLength={6}
                pattern="\d{6}"
              />
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setTransferStep('confirm');
                    setTransferOtp('');
                  }}
                >
                  Back
                </Button>
                <Button type="submit" disabled={transferSubmitting || transferOtp.length !== 6}>
                  {transferSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Confirm transfer
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
